import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { Users, ShieldCheck, EyeOff, HandHeart, CheckCircle2, Copy } from "lucide-react";

const NEED_STEPS = [
  "Make your declaration.",
  "Verify your email.",
  "Ask WitnessYourVote for a witness.",
  "A participating witness can accept your request.",
  "They confirm that you made and signed your declaration.",
];

export default function WitnessNetwork() {
  const [munis, setMunis] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", municipality_id: "", ward: "" });
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    api.get("/public/municipalities").then((r) => setMunis(r.data)).catch(() => {});
  }, []);

  const signup = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/public/witness-network/volunteer", { ...form, ward: form.ward || null, website: "" });
      setResult(r.data);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sign-up failed. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const copyLink = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch { toast.error("Could not copy"); }
  };

  if (result) {
    return (
      <div className="wyv-container py-12 max-w-xl">
        <Card className="border-emerald-200" data-testid="volunteer-success-card">
          <CardContent className="p-8">
            <div className="h-12 w-12 rounded-full bg-emerald-100 flex items-center justify-center mb-4">
              <CheckCircle2 className="h-6 w-6 text-emerald-700" />
            </div>
            <h1 className="font-heading font-bold text-2xl text-foreground">You're on the Witness Network</h1>
            {result.verification_required && (
              <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4" data-testid="volunteer-verify-note">
                One more step: confirm your email ({result.identity?.email_masked}). We sent you a verification
                link. You can accept witness requests once your email is verified.
              </p>
            )}
            <p className="text-sm text-muted-foreground mt-4">
              Bookmark your private volunteer page — witness requests appear there:
            </p>
            <p className="font-mono text-[11px] text-muted-foreground break-all mt-2" data-testid="volunteer-url">{result.volunteer_url}</p>
            <div className="flex flex-wrap gap-2.5 mt-4">
              <Button size="sm" onClick={() => copyLink(result.volunteer_url)} data-testid="copy-volunteer-link">
                <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy My Volunteer Link
              </Button>
              <Button asChild size="sm" variant="outline" data-testid="open-volunteer-page">
                <a href={result.volunteer_url}>Open My Volunteer Page</a>
              </Button>
            </div>
            <p className="text-[11px] text-muted-foreground mt-5">{result.safety_note}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="wyv-container py-12 sm:py-16">
      <div className="max-w-2xl">
        <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-4">Witness Network</Badge>
        <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="witness-network-title">
          Need a witness? We can help.
        </h1>
        <p className="text-muted-foreground mt-3 leading-relaxed">
          You don't need to find someone before you start. WitnessYourVote can match you with a participating
          witness in your municipality — privately and digitally.
        </p>
      </div>

      <div className="mt-10 grid lg:grid-cols-2 gap-8 items-start">
        {/* I NEED A WITNESS */}
        <Card data-testid="need-witness-card">
          <CardContent className="p-6 sm:p-8">
            <h2 className="font-heading font-bold text-xl text-foreground inline-flex items-center gap-2">
              <Users className="h-5 w-5 text-accent" /> I need a witness
            </h2>
            <ol className="mt-5 space-y-3">
              {NEED_STEPS.map((s, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-foreground">
                  <span className="h-6 w-6 shrink-0 rounded-full bg-secondary text-primary text-xs font-bold flex items-center justify-center">{i + 1}</span>
                  {s}
                </li>
              ))}
            </ol>
            <p className="text-xs text-muted-foreground mt-4 flex items-start gap-2">
              <EyeOff className="h-4 w-4 text-accent shrink-0 mt-0.5" /> They do not see your official ballot.
            </p>
            <Button asChild className="mt-5 bg-accent hover:bg-accent/90 w-full h-11" data-testid="need-witness-declare-cta">
              <Link to="/declare">Start — Make My Declaration</Link>
            </Button>
            <p className="text-[11px] text-muted-foreground mt-3">
              Already declared? Open your declaration page (from your receipt) and press
              <span className="font-semibold"> “Find Me a Witness”</span>.
            </p>
          </CardContent>
        </Card>

        {/* I CAN BE A WITNESS */}
        <Card data-testid="be-witness-card">
          <CardContent className="p-6 sm:p-8">
            <h2 className="font-heading font-bold text-xl text-foreground inline-flex items-center gap-2">
              <HandHeart className="h-5 w-5 text-accent" /> I can be a witness
            </h2>
            <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
              Help neighbours strengthen their declarations. You confirm a person made and signed their
              declaration — you never see their ballot. You can pause or leave anytime.
            </p>
            <form onSubmit={signup} className="mt-5 space-y-4">
              <div>
                <Label htmlFor="v-name">Your full name</Label>
                <Input id="v-name" className="mt-1.5" value={form.name}
                       onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="volunteer-name-input" />
              </div>
              <div>
                <Label htmlFor="v-email">Your email</Label>
                <Input id="v-email" type="email" className="mt-1.5" value={form.email}
                       onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="volunteer-email-input" />
                <p className="text-[11px] text-muted-foreground mt-1">Kept private. Never shown to other participants.</p>
              </div>
              <div>
                <Label>Municipality</Label>
                <Select value={form.municipality_id || undefined} onValueChange={(v) => setForm({ ...form, municipality_id: v })}>
                  <SelectTrigger className="mt-1.5 bg-white" data-testid="volunteer-muni-select"><SelectValue placeholder="Choose your municipality" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {munis.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="v-ward">Ward (optional)</Label>
                <Input id="v-ward" className="mt-1.5" placeholder="e.g. Ward 8" value={form.ward}
                       onChange={(e) => setForm({ ...form, ward: e.target.value })} data-testid="volunteer-ward-input" />
              </div>
              <Button type="submit" className="w-full h-11" disabled={busy || !form.name.trim() || !form.email.trim() || !form.municipality_id} data-testid="volunteer-submit">
                <ShieldCheck className="h-4 w-4 mr-2" /> {busy ? "Joining…" : "JOIN THE WITNESS NETWORK"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <Card className="mt-8 bg-secondary max-w-3xl">
        <CardContent className="p-5 text-xs text-muted-foreground leading-relaxed" data-testid="witness-network-safety">
          WitnessYourVote acts as the intermediary. Never share your home address, phone number or unnecessary
          personal information — witnessing is completed digitally through a secure link. The Witness Network is
          not a public directory: identities, addresses and declarations are never listed publicly. WitnessYourVote
          is an independent voluntary public-interest platform, not an election authority.
        </CardContent>
      </Card>
    </div>
  );
}
